'use client';

import { FileText, Play, X } from 'lucide-react';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import type { UploadClawFileResult } from '../../../types';

interface DraftAttachmentCardProps {
  file: UploadClawFileResult;
  onClick?: () => void;
  onRemove?: () => void;
  removing?: boolean;
}

export const DraftAttachmentCard = ({ file, onClick, onRemove, removing = false }: DraftAttachmentCardProps) => {
  const isVisual = file.file_type === 'image' || file.file_type === 'video';
  const fileName = file.file_name || file.file_path.split('/').filter(Boolean).pop() || file.file_path;

  return (
    <div className="group w-[120px] shrink-0 overflow-hidden rounded-12 border border-system-white08 bg-[rgba(255,255,255,0.06)]">
      <div
        className="relative aspect-square w-full cursor-pointer overflow-hidden bg-[rgba(255,255,255,0.04)] text-left"
        onClick={onClick}
      >
        {file.preview_url && isVisual ? (
          file.file_type === 'image' ? (
            <img alt={fileName} src={file.preview_url} className="size-full object-cover" />
          ) : (
            <>
              <video className="size-full object-cover" muted preload="metadata" src={file.preview_url} />
              <div className="absolute inset-0 bg-black/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex size-9 items-center justify-center rounded-full bg-black/40 text-system-white">
                  <Play className="ml-0.5 size-4 fill-current" />
                </div>
              </div>
            </>
          )
        ) : file.file_type === 'image' ? (
          <div className="flex size-full items-center justify-center text-system-white40">
            <ProductLinePic className="size-8" />
          </div>
        ) : file.file_type === 'video' ? (
          <div className="flex size-full items-center justify-center text-system-white40">
            <ProductLineAiVideo className="size-8" />
          </div>
        ) : file.file_type === 'audio' ? (
          <div className="flex size-full flex-col items-center justify-center gap-2 text-system-white40">
            <div className="flex size-14 items-center justify-center rounded-12 bg-system-white08">
              <BaseLineMusic className="size-7" />
            </div>
            <div className="text-[11px] text-system-white24 uppercase tracking-[0.12em]">Preview</div>
          </div>
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-2 text-system-white40">
            <div className="flex size-14 items-center justify-center rounded-12 bg-system-white08">
              <FileText className="size-7" />
            </div>
            <div className="text-[11px] text-system-white24 uppercase tracking-[0.12em]">Preview</div>
          </div>
        )}

        <button
          type="button"
          disabled={removing}
          className="absolute top-2 right-2 flex size-6 items-center justify-center rounded-full bg-black/50 text-system-white transition hover:bg-black/70"
          onClick={(event) => {
            event.stopPropagation();
            onRemove?.();
          }}
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
};
